import type { FC } from "react";

import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import type { ProductNicotineStrength } from "@jti/magento";
import { ProductMiniature, QuantityInput, type QuantityInputProps, useTranslations } from "@jti/ui";

import { ConsentItemFlag } from "@checkout/components/CartItem/ItemFlag";
import { RemoveCartItemButton } from "@checkout/components/CartItem/RemoveCartItemButton";

import styles from "./ProductInfoAndActions.module.scss";

export type ProductInfoAndActionsProps = {
  imageUrl: string;
  name: string;
  outOfStock: boolean;
  price: {
    currency: string;
    value: number;
  };
  url?: string;
  hasRegistrationConsent?: boolean;
  isQuantityInputVisible?: boolean;
  isQuantityLimitReached: boolean;
  isRemovable?: boolean;
  quantity: QuantityInputProps;
  quantityLimit: number;
  removeItemHandler: () => void;
  isSubscription?: boolean;
  nicotineStrength?: ProductNicotineStrength;
};

export const ProductInfoAndActions: FC<ProductInfoAndActionsProps> = ({
  imageUrl,
  name,
  outOfStock,
  price,
  url,
  hasRegistrationConsent,
  isQuantityInputVisible = true,
  isQuantityLimitReached,
  isRemovable = true,
  quantity,
  quantityLimit,
  removeItemHandler,
  isSubscription = false,
  nicotineStrength,
}) => {
  const { __ } = useTranslations();

  return (
    <div className={styles.wrapper}>
      <div className={styles.productInfoContainer}>
        <ProductMiniature
          variant="compact"
          url={url}
          name={name}
          imageUrl={imageUrl}
          outOfStock={outOfStock}
          price={price}
          isSubscription={isSubscription}
          nicotineStrength={nicotineStrength}
        >
          {hasRegistrationConsent && <ConsentItemFlag />}
        </ProductMiniature>
      </div>

      <div className={styles.actions}>
        {isQuantityInputVisible && <QuantityInput {...quantity} />}
        {isRemovable && (
          <div className={styles.removeProductButtonContainer}>
            <RemoveCartItemButton onClick={removeItemHandler} size="sm" />
          </div>
        )}
      </div>

      {isQuantityLimitReached && (
        <AlertBanner tone="warning" description={`${__("libs.ui.notification.limit.sku")} (${quantityLimit})`} />
      )}
    </div>
  );
};
